import type { Action, CreateAssessmentResult } from './types'

const isProd = process.env.NODE_ENV === 'production'
/**
 * The test key returns the some score for every token, it's more of a health check and saves money
 */
export const scoreSiteKey = isProd
	? process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
	: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TEST

export const checkboxSiteKey = isProd
	? process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_CHECKBOX
	: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_CHECKBOX_TEST

export const RECAPTCHA_SCORE_URL = `https://www.google.com/recaptcha/enterprise.js?render=${scoreSiteKey}`
export const RECAPTCHA_CHECKBOX_URL = `https://www.google.com/recaptcha/api.js?render=explicit`
export const PROXY_ENDPOINT = '/api/assess-recaptcha-token'
export const BOT_THRESHOLD = 0.5

export const ACTIONS: Record<string, Action> = {
	CAREERS_SUBMIT_APPLICATION: 'careers_submit_application',
} as const

type AssessUserParams = {
	action: Action
	ctx?: Record<string, any>
}
/**
 * Assesses the current user using reCAPTCHA invisble Enterprise to determine if they are likely human
 * For more information see: https://cloud.google.com/recaptcha/docs/interpret-assessment-website?hl=en
 * @param action - The action being assessed
 * @returns CreateAssessmentResult containing either a score or error
 */
export const assessUserInvisible = async ({
	action,
	ctx,
}: AssessUserParams): Promise<CreateAssessmentResult> => {
	return new Promise((resolve) => {
		window.grecaptcha.enterprise.ready(async () => {
			try {
				const token = await window.grecaptcha.enterprise.execute(scoreSiteKey, {
					action,
				})
				const result = await fetchAssessment({
					siteKey: scoreSiteKey,
					token,
					action,
					ctx,
					isCheckbox: false,
				})
				resolve(result)
			} catch (error) {
				console.error(`Failed to execute recaptcha for ${action}`, error)
				resolve({ error: 'Failed to execute recaptcha assessment' })
			}
		})
	})
}

type AssessUserCheckboxParams = AssessUserParams & {
	containerId: string
}

export const assessUserCheckbox = async ({
	action,
	ctx,
	containerId,
}: AssessUserCheckboxParams): Promise<CreateAssessmentResult> => {
	return new Promise((resolve) => {
		window.grecaptcha.enterprise.ready(async () => {
			const widgetId = window.grecaptcha.enterprise.render(containerId, {
				sitekey: checkboxSiteKey,
				callback: async (token) => {
					try {
						const result = await fetchAssessment({
							siteKey: checkboxSiteKey,
							token,
							action,
							ctx,
							isCheckbox: true,
						})
						resolve(result)
					} catch (error) {
						resolve({ error })
					}
				},
				'error-callback': (err) => {
					resolve({
						error:
							err ?? 'UNKNOWN ERROR: Failed to execute recaptcha assessment',
					})
				},
			})

			window.grecaptcha.enterprise.reset(widgetId)
		})
	})
}

const isValidAssessmentResult = (
	result: any
): result is CreateAssessmentResult => {
	if ('score' in result) {
		return typeof result.score === 'number'
	}
	return false
}

type FetchAssessmentParams = {
	siteKey: string
	token: string
	action: Action
	ctx?: Record<string, any>
	isCheckbox: boolean
}

/**
 * Proxied request to google recaptcha API route
 * Prevents us from leaking our recaptcha API key to the client
 * Error handling left for the caller
 */
export const fetchAssessment = async ({
	siteKey,
	token,
	action,
	ctx,
	isCheckbox,
}: FetchAssessmentParams): Promise<CreateAssessmentResult> => {
	try {
		const response = await fetch(PROXY_ENDPOINT, {
			method: 'POST',
			body: JSON.stringify({ siteKey, token, action, ctx, isCheckbox }),
			headers: {
				'Content-Type': 'application/json',
			},
		})

		if (!response.ok) {
			const errRes = await response.text().catch(() => 'Unknown error')
			return {
				error: `${errRes} 
        ACTION: ${action}`,
			}
		}

		const jsonRes = await response.json()
		if (!isValidAssessmentResult(jsonRes)) {
			const errMsg = `Failed to parse recaptcha assessment result for action "${action}"`
			return { error: errMsg }
		}

		return jsonRes
	} catch (e) {
		return { error: 'Failed to fetch recaptcha assessment' }
	}
}

export const isValidAction = (maybeAction: string): maybeAction is Action => {
	return Object.values(ACTIONS).includes(maybeAction as Action)
}
